import React, { createRef } from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import Branch from 'components/Form/Branch';
import Textarea from 'components/Form/Textarea';
import Layout from 'components/Form/PVQLocation/Layouts';
import Text from 'components/Form/Text';
import PVQName from 'components/Form/PVQName';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
} from 'types/props';
import PVQLocation from 'components/Form/PVQLocation';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import { lienEntityTypes, lienPropertyTypes, lienReasonOptions } from 'constants/enums/financialOptions';
import { FinancialLienItemModel } from 'forms/sections/financial/lien/types';
import NotApplicable from 'components/Form/NotApplicable';
import { Dispatch } from 'redux';

interface Props extends Partial<FinancialLienItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  dispatch?: Dispatch
  scrollIntoView?: CommonScrollIntoView
}

export default class LienItem extends ValidationElement<Props> {
  clearableTextAreaRef: React.RefObject<ValidationElement>;

  constructor(props) {
    super(props);

    this.clearableTextAreaRef = createRef();
    this.clearTextArea = this.clearTextArea.bind(this);
  }

  update = (queue: Partial<FinancialLienItemModel>) => {
    this.props.onUpdate({
      EntityType: this.props.EntityType,
      EntityTypeExplain: this.props.EntityTypeExplain,
      EntityName: this.props.EntityName,
      PrivateName: this.props.PrivateName,
      PropertyType: this.props.PropertyType,
      PropertyTypeExplain: this.props.PropertyTypeExplain,
      LienReason: this.props.LienReason,
      WhyExplain: this.props.WhyExplain,
      AmountRequiredToPay: this.props.AmountRequiredToPay,
      When: this.props.When,
      Current: this.props.Current,
      WhenResolved: this.props.WhenResolved,
      ActionsTaken: this.props.ActionsTaken,
      ActionsTakenNone: this.props.ActionsTakenNone,
      ActionsTakenReason: this.props.ActionsTakenReason,
      OfficeName: this.props.OfficeName,
      OfficeLocation: this.props.OfficeLocation,
      CaseNum: this.props.CaseNum,
      CaseNumIdk: this.props.CaseNumIdk,
      ...queue,
    });
  };

  clearTextArea = () => {
    if (this.clearableTextAreaRef.current) {
      this.clearableTextAreaRef.current.clearValue();
    }
  };

  updateEntityType = (
    values: FinancialLienItemModel['EntityType']
  ) => {
    this.update({
      EntityType: values,
    });
  };

  updateEntityTypeExplain = (
    values: FinancialLienItemModel['EntityTypeExplain']
  ) => {
    this.update({
      EntityTypeExplain: values,
    });
  };

  updateEntityName = (
    values: FinancialLienItemModel['EntityName']
  ) => {
    this.update({
      EntityName: values,
    });
  };

  updatePrivateName = (
    values: FinancialLienItemModel['PrivateName']
  ) => {
    this.update({
      PrivateName: values,
    });
  };

  updatePropertyType = (
    values: FinancialLienItemModel['PropertyType']
  ) => {
    this.update({
      PropertyType: values,
    });
  };

  updatePropertyTypeExplain = (
    values: FinancialLienItemModel['PropertyTypeExplain']
  ) => {
    this.update({
      PropertyTypeExplain: values,
    });
  };

  updateLienReason = (
    values: FinancialLienItemModel['LienReason']
  ) => {
    this.update({
      LienReason: values,
    });
  };

  updateWhyExplain = (
    values: FinancialLienItemModel['WhyExplain']
  ) => {
    this.update({
      WhyExplain: values,
    });
  };

  updateAmountRequiredToPay = (
    values: FinancialLienItemModel['AmountRequiredToPay']
  ) => {
    this.update({
      AmountRequiredToPay: values,
    });
  };

  updateWhen = (
    values: FinancialLienItemModel['When']
  ) => {
    this.update({
      When: values,
    });
  };

  updateCurrent = (
    values: FinancialLienItemModel['Current']
  ) => {
    this.update({
      Current: values,
    });
  };

  updateWhenResolved = (
    values: FinancialLienItemModel['WhenResolved']
  ) => {
    this.update({
      WhenResolved: values,
    });
  };

  updateOfficeName = (
    values: FinancialLienItemModel['OfficeName']
  ) => {
    this.update({
      OfficeName: values,
    });
  };

  updateOfficeLocation = (
    values: FinancialLienItemModel['OfficeLocation']
  ) => {
    this.update({
      OfficeLocation: values,
    });
  };

  updateCaseNum = (
    values: FinancialLienItemModel['CaseNum']
  ) => {
    this.update({
      CaseNum: values,
    });
  };

  updateCaseNumIdk = (
    values: FinancialLienItemModel['CaseNumIdk']
  ) => {
    this.update({
      CaseNum: values.applicable ? this.props.CaseNum : {},
      CaseNumIdk: values,
    });
  };

  updateActionsTaken = (values: FinancialLienItemModel['ActionsTaken']) => {
    this.update({
      ActionsTaken: values,
    });
  };

  updateActionsTakenReason = (values: FinancialLienItemModel['ActionsTakenReason']) => {
    this.update({
      ActionsTakenReason: values,
    });
  };

  updateActionsTakenNone = (values: FinancialLienItemModel['ActionsTakenNone']) => {
    if (values?.applicable === false) {
      this.clearTextArea();
    }

    this.update({
      ActionsTaken: values?.applicable === false ? {} as FinancialLienItemModel['ActionsTaken'] : this.props.ActionsTaken,
      ActionsTakenNone: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    return (
      <div className="lien-item">
        <Field
          exactAttribute
          title={i18n.t('financial.pvq.lien.branch.ifYes.entityTypeRadioGroup.title')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.EntityType`}
        >
          <RadioGroup
            {...this.props.EntityType}
            className="option-list option-list-vertical"
            selectedValue={(this.props.EntityType || {}).value}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.EntityTypeRadioGroup`}
          >
            {lienEntityTypes.map((entityType) => (
              <Radio
                key={entityType}
                label={entityType}
                value={entityType}
                onUpdate={this.updateEntityType}
              />
            ))}
          </RadioGroup>
        </Field>
        {(this.props.EntityType?.value === 'Government Agency' || this.props.EntityType?.value === 'Company') && (
          <Field
            title={i18n.t('financial.pvq.lien.branch.ifYes.entityTypeRadioGroup.ifCompGovOrEntityText')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.EntityName`}
          >
            <Text
              {...this.props.EntityName}
              attribute={`${attribute}.EntityName`}
              name="entityname"
              onUpdate={this.updateEntityName}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Field>
        )}
        {this.props.EntityType?.value === 'Individual' && (
          <Field
            title={i18n.t('financial.pvq.lien.branch.ifYes.entityTypeRadioGroup.ifPrivateText')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.PrivateName`}
          >
            <PVQName
              {...this.props.PrivateName}
              attribute={`${attribute}.PrivateName`}
              name="PrivateName"
              onUpdate={this.updatePrivateName}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              showLastFirstMiddleNameOrder
            />
          </Field>
        )}
        {this.props.EntityType?.value === 'Other' && (
          <Field
            title={i18n.t('financial.pvq.lien.branch.ifYes.entityTypeRadioGroup.ifOther')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.EntityTypeExplain`}
          >
            <Textarea
              {...this.props.EntityTypeExplain}
              attribute={`${attribute}.EntityTypeExplain`}
              name="EntityTypeExplain"
              onUpdate={this.updateEntityTypeExplain}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Field>
        )}

        <Field
          exactAttribute
          title={i18n.t('financial.pvq.lien.branch.ifYes.propertyTypeRadioGroup.title')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.PropertyType`}
        >
          <RadioGroup
            {...this.props.PropertyType}
            className="option-list option-list-vertical"
            selectedValue={(this.props.PropertyType || {}).value}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.PropertyTypeRadioGroup`}
          >
            {lienPropertyTypes.map((propertyType) => (
              <Radio
                key={propertyType}
                label={propertyType}
                value={propertyType}
                onUpdate={this.updatePropertyType}
              />
            ))}
          </RadioGroup>
        </Field>
        {this.props.PropertyType?.value.startsWith('Other') && (
          <Field
            title={i18n.t('financial.pvq.lien.branch.ifYes.propertyTypeRadioGroup.ifOther')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.PropertyTypeExplain`}
          >
            <Textarea
              {...this.props.PropertyTypeExplain}
              attribute={`${attribute}.PropertyTypeExplain`}
              name="propertytypeexplain"
              onUpdate={this.updatePropertyTypeExplain}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.pvq.lien.branch.ifYes.whyRadioGroup.title')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.LienReason`}
        >
          <RadioGroup
            {...this.props.LienReason}
            className="option-list option-list-vertical"
            selectedValue={(this.props.LienReason || {}).value}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.LienReason`}
          >
            {lienReasonOptions.map((reason) => (
              <Radio
                key={reason}
                label={reason}
                value={reason}
                onUpdate={this.updateLienReason}
              />
            ))}
          </RadioGroup>
        </Field>
        {this.props.LienReason?.value === 'Other' && (
          <Field
            title={i18n.t('financial.pvq.lien.branch.ifYes.whyRadioGroup.ifOther')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.WhyExplain`}
          >
            <Textarea
              {...this.props.WhyExplain}
              attribute={`${attribute}.WhyExplain`}
              name="whyexplain"
              onUpdate={this.updateWhyExplain}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.pvq.lien.branch.ifYes.amountCurrency')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.AmountRequiredToPay`}
        >
          <Currency
            maxLength="10"
            {...this.props.AmountRequiredToPay}
            attribute={`${attribute}.AmountRequiredToPay`}
            name="amountrequiredtopay"
            onUpdate={this.updateAmountRequiredToPay}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.lien.branch.ifYes.whenDateEstimated')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.When`}
        >
          <DateControl
            {...this.props.When}
            attribute={`${attribute}.When`}
            name="when"
            onUpdate={this.updateWhen}
            onError={this.props.onError}
            hideDay
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            showEstimated={true}
          />
        </Field>

        <Branch
          {...this.props.Current}
          name="current"
          label={i18n.t('financial.pvq.lien.branch.ifYes.branch.title')}
          labelSize="h4"
          className="lien-current-branch"
          onUpdate={this.updateCurrent}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.handleError}
          attribute={`${attribute}.Current`}
        />

        {this.props.Current?.value === 'No' && (
          <Field
            title={i18n.t('financial.pvq.lien.branch.ifYes.branch.ifNo.whenDateEstimated')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.WhenResolved`}
          >
            <DateControl
              {...this.props.WhenResolved}
              attribute={`${attribute}.WhenResolved`}
              name="whenresolved"
              onUpdate={this.updateWhenResolved}
              onError={this.props.onError}
              hideDay
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              showEstimated={true}
            />
          </Field>
        )}

        {this.props.Current?.value === 'Yes' && (
          <Field
            title={i18n.t('financial.pvq.lien.branch.ifYes.branch.ifYes.combobox.actionsTaken')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ActionsTaken`}
          >
            <NotApplicable
              {...this.props.ActionsTakenNone}
              label={i18n.t('financial.pvq.shared.none')}
              attribute={`${attribute}.ActionsTakenNone`}
              onUpdate={this.updateActionsTakenNone}
              scrollIntoView={this.props.scrollIntoView}
              required={this.props.required}
            >
              <Textarea
                {...this.props.ActionsTaken}
                ref={this.clearableTextAreaRef}
                attribute={`${attribute}.ActionsTaken`}
                scrollIntoView={this.props.scrollIntoView}
                onError={this.props.onError}
                onUpdate={this.updateActionsTaken}
                required={this.props.required}
              />
            </NotApplicable>
          </Field>
        )}

        {this.props.ActionsTakenNone?.applicable === false && this.props.Current?.value === 'Yes' && (
          <Field
            title={i18n.t('financial.pvq.lien.branch.ifYes.branch.ifYes.combobox.ifNone')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ActionsTakenReason`}
          >
            <Textarea
              {...this.props.ActionsTakenReason}
              attribute={`${attribute}.ActionsTakenReason`}
              scrollIntoView={this.props.scrollIntoView}
              onUpdate={this.updateActionsTakenReason}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.pvq.lien.branch.ifYes.officeNameText')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.OfficeName`}
        >
          <Textarea
            {...this.props.OfficeName}
            attribute={`${attribute}.OfficeName`}
            name="OfficeName"
            onUpdate={this.updateOfficeName}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.OfficeLocation`}
          title={i18n.t('financial.pvq.lien.branch.ifYes.officeLocation')}
          titleSize="h4"
        >
          <PVQLocation
            {...this.props.OfficeLocation}
            attribute={`${attribute}.OfficeLocation`}
            name="OfficeLocation"
            layout={Layout.US_ADDRESS}
            country="United States"
            onUpdate={this.updateOfficeLocation}
            dispatch={this.props.dispatch}
            excludeUnitedStates={false}
            geocode
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            showEstimated={false}
            domesticTitle=" "
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.lien.branch.ifYes.caseNumText')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CaseNum`}
        >
          <NotApplicable
            {...this.props.CaseNumIdk}
            name="casenumidk"
            label={i18n.t('financial.pvq.shared.idk')}
            onUpdate={this.updateCaseNumIdk}
            attribute={`${attribute}.CaseNumIdk`}
          >
            <Text
              {...this.props.CaseNum}
              attribute={`${attribute}.CaseNum`}
              name="casenum"
              onUpdate={this.updateCaseNum}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </NotApplicable>
        </Field>
      </div>
    );
  }
}
LienItem.defaultProps = {
  dispatch: () => { },
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
  required: false,
};
